import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import Sidebar from '../components/Sidebar';
import CreditReportViewer from '../components/CreditReportViewer';
import { RefreshCw } from 'lucide-react';

const StatCard = ({ title, value, sub, color = 'primary', blink }) => {
  const colors = { primary: 'text-primary', emerald: 'text-emerald-500', amber: 'text-amber-500' };
  return (
    <div className={`stat-card animate-fade-in-up ${blink === 'success' ? 'blink-success' : blink === 'failure' ? 'blink-failure' : ''}`} data-accent={color}>
      <p className="text-xs text-muted-foreground font-medium mb-2">{title}</p>
      <p className={`text-3xl font-extrabold ${colors[color]}`}>{value ?? '—'}</p>
      {sub && <p className="text-xs text-muted-foreground mt-2">{sub}</p>}
    </div>
  );
};

export default function NGODashboard() {
  const { t, apiCall, user } = useApp();
  const [activeTab, setActiveTab] = useState('overview');
  const [reportStatus, setReportStatus] = useState(null);

  const handleReportResult = (status) => {
    setReportStatus(status);
    setTimeout(() => setReportStatus(null), 4000);
  };
  const [stats, setStats] = useState({});
  const [wallet, setWallet] = useState({ balance: 0 });
  const [usageLogs, setUsageLogs] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [logoSettings, setLogoSettings] = useState({});
  const [settings, setSettings] = useState({});

  const loadBase = useCallback(async () => {
    try {
      const [pub, ovw, wal] = await Promise.all([
        apiCall('GET', '/settings/public'),
        apiCall('GET', '/ngo/overview'),
        apiCall('GET', '/ngo/wallet')
      ]);
      setLogoSettings(pub); setSettings(pub); setStats(ovw); setWallet(wal);
    } catch {}
  }, [apiCall]);

  useEffect(() => { loadBase(); }, [loadBase]);

  useEffect(() => {
    const loaders = {
      'usage-logs': () => apiCall('GET', '/ngo/usage-logs').then(r => setUsageLogs(r.logs)),
      'ledger': () => apiCall('GET', '/ngo/ledger').then(r => setLedger(r.ledger)),
      overview: () => Promise.all([
        apiCall('GET', '/ngo/overview').then(setStats),
        apiCall('GET', '/ngo/wallet').then(setWallet),
      ]).catch(() => {}),
    };
    if (loaders[activeTab]) loaders[activeTab]().catch(() => {});
  }, [activeTab, apiCall]);

  // Real-time overview polling (30s)
  useEffect(() => {
    if (activeTab !== 'overview') return;
    const timer = setInterval(() => {
      Promise.all([
        apiCall('GET', '/ngo/overview').then(setStats),
        apiCall('GET', '/ngo/wallet').then(setWallet),
      ]).catch(() => {});
    }, 30000);
    return () => clearInterval(timer);
  }, [activeTab, apiCall]);

  const renderOverview = () => (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
          Welcome back, {user?.name || 'NGO User'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Here's your credit usage overview</p>
      </div>
      <div data-testid="ngo-credit-balance" className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/8 border border-primary/20 rounded-xl mb-6">
        <span className="text-sm font-bold text-primary">{wallet.balance} Credits Available</span>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title={t('walletBalance')} value={wallet.balance} sub="credits" />
        <StatCard title={t('totalAssigned')} value={wallet.total_assigned_in} color="emerald" />
        <StatCard title={t('totalUsed')} value={wallet.total_used} color="amber" />
        <StatCard title={t('totalReports')} value={stats.reports_pulled} blink={reportStatus} />
        <StatCard title={t('totalDownloads')} value={stats.reports_downloaded} color="emerald" />
      </div>
      {wallet.balance === 0 && (
        <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl text-sm text-amber-600 dark:text-amber-400">
          Your wallet is empty. Please contact your admin to assign credits.
        </div>
      )}
    </div>
  );

  const renderUsageLogs = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm">{t('usageLogs')}</h3>
        <button onClick={() => apiCall('GET', '/ngo/usage-logs').then(r => setUsageLogs(r.logs))}
          className="btn-secondary text-xs flex items-center gap-1.5"><RefreshCw size={12} />{t('refresh')}</button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
          {['Subject', 'Mobile', 'Credits Used', 'Demo', t('timestamp')].map(h => (
            <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium">{h}</th>
          ))}
        </tr></thead><tbody>
          {usageLogs.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">{t('noLogs')}</td></tr> :
            usageLogs.map(log => (
              <tr key={log.id} className="border-b border-border/50 hover:bg-secondary/20">
                <td className="px-3 py-2 font-medium">{log.subject_name}</td>
                <td className="px-3 py-2 font-mono-code">{log.subject_mobile}</td>
                <td className="px-3 py-2 text-destructive font-medium">-{log.credits_deducted}</td>
                <td className="px-3 py-2">{log.is_mock ? <span className="text-amber-500 text-xs font-medium">DEMO</span> : '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{new Date(log.created_at).toLocaleString()}</td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );

  const renderLedger = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-sm">{t('creditLedger')}</h3>
        <button onClick={() => apiCall('GET', '/ngo/ledger').then(r => setLedger(r.ledger))}
          className="btn-secondary text-xs flex items-center gap-1.5"><RefreshCw size={12} />{t('refresh')}</button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-xs"><thead><tr className="border-b border-border bg-secondary/30">
          {[t('type'), t('amount'), t('description'), t('balanceAfter'), t('date')].map(h => (
            <th key={h} className="text-left px-3 py-2 text-muted-foreground font-medium">{h}</th>
          ))}
        </tr></thead><tbody>
          {ledger.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">{t('noData')}</td></tr> :
            ledger.map((e, i) => (
              <tr key={i} className="border-b border-border/50 hover:bg-secondary/20">
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${e.transaction_type === 'credit' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'}`}>
                    {e.transaction_type === 'credit' ? '+' : '-'}{e.amount}
                  </span>
                </td>
                <td className="px-3 py-2 font-bold">{e.amount}</td>
                <td className="px-3 py-2 text-muted-foreground max-w-xs truncate">{e.description}</td>
                <td className="px-3 py-2 font-bold text-primary">{e.balance_after}</td>
                <td className="px-3 py-2 text-muted-foreground">{new Date(e.created_at).toLocaleString()}</td>
              </tr>
            ))}
        </tbody></table>
      </div>
    </div>
  );

  const TABS = {
    'overview': renderOverview,
    'credit-report': () => <CreditReportViewer consentText={settings.checkbox_consent_content} pdfFooterNote={settings.pdf_footer_note} onReportResult={handleReportResult} />,
    'usage-logs': renderUsageLogs,
    'ledger': renderLedger,
  };

  return (
    <div className="min-h-screen bg-background" data-testid="ngo-dashboard">
      <Sidebar role="ngo" activeTab={activeTab} setActiveTab={setActiveTab} logoSettings={logoSettings} />
      <main className="lg:pl-64 pt-14 lg:pt-4 lg:pr-4 lg:pb-4">
        <div className="bg-white dark:bg-card lg:rounded-2xl shadow-sm dark:shadow-none min-h-[calc(100vh-2rem)] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-100 dark:border-border bg-white dark:bg-card shrink-0">
            <span className="text-sm font-semibold text-foreground">
              {activeTab.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
            </span>
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                {user?.name?.[0]?.toUpperCase() || 'N'}
              </div>
              <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || 'NGO User'}</span>
            </div>
          </div>
          <div className="flex-1 p-6 lg:p-8">
            {(TABS[activeTab] || (() => <div>Coming soon</div>))()}
          </div>
        </div>
      </main>
    </div>
  );
}
